import { Fee } from '@kinet-open-network/client/fees/data-access';
import {
  ApiDateFormat,
  BaseModel,
} from '@kinet-open-network/client/shared/data-access/model';
import {
  AbilitySubjectType,
  DiscountType,
  ICustomerFee,
  IItemPrice,
} from '@kinet-open-network/shared/data-access/model';
import { Type } from 'class-transformer';
import { endOfDay } from 'date-fns';

export class CustomerFee
  extends BaseModel<CustomerFee, 'CustomerFee'>
  implements ICustomerFee
{
  static override readonly modelName: AbilitySubjectType = 'CustomerFee';

  price!: number;
  apId!: string;
  feeId!: string;
  servPackageId?: string | undefined;
  customerId!: number;
  @Type(() => Fee)
  fee!: Fee;
  @Type(() => Date)
  activationDate!: Date | null;
  @Type(() => Date)
  deactivationDate!: Date | null;
  discountType?: DiscountType | null;
  discountValue?: number | null;
  @ApiDateFormat('yyyy-MM-dd')
  @Type(() => Date)
  discountTo?: Date | null;
  discount?: boolean;
  invoiceId!: string | null;
  @Type(() => Date)
  invoiceDate!: Date | null;
  @Type(() => Date)
  invoiceFrom!: Date | null;
  @Type(() => Date)
  invoiceTo!: Date | null;
  note?: string | null;

  get prices(): IItemPrice[] {
    return this.fee?.prices ?? [];
  }

  getActivePriceAt(date: Date | number = new Date()): IItemPrice | null {
    return this.fee?.getActivePriceAt(date) ?? null;
  }

  hasDiscountAt(date: Date | number = new Date()): boolean {
    return (
      !!this.discountValue &&
      (!this.discountTo || endOfDay(this.discountTo) > new Date(date))
    );
  }

  getDiscountAt(date: Date | number = new Date()): number {
    if (!this.hasDiscountAt(date)) {
      return 0;
    }

    const priceValue = this.getActivePriceAt(date)?.price ?? 0;

    switch (this.discountType) {
      case DiscountType.Percentage:
        return (
          (priceValue * Math.min(100, Math.max(0, this.discountValue ?? 0))) /
          100
        );
      case DiscountType.Overwrite:
        // discountValue is the new overwritten price; discount = difference
        return Math.max(0, priceValue - (this.discountValue ?? 0));
      case DiscountType.Fixed:
        return Math.min(priceValue, this.discountValue ?? 0);
      default:
        return 0;
    }
  }

  getPriceAt(date: Date | number = new Date()): number {
    const price = this.getActivePriceAt(date)?.price ?? 0;

    if (this.hasDiscountAt(date)) {
      return price - this.getDiscountAt(date);
    }

    return price;
  }

  get invoiced(): boolean {
    return !!this.invoiceDate;
  }

  get deactivated() {
    return !!this.deactivationDate && this.deactivationDate <= new Date();
  }
}
